import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial';
import { BleClient } from '@capacitor-community/bluetooth-le';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { setPrintQueueInstance } from '../Utils/PrintQueueHelper';

interface PrintJob {
    id: string;
    tipo: 'ble' | 'spp' | 'rede' | 'local';
    dados: any;
    comandosEscPos: Uint8Array;
    impressoraConfig: {
        deviceId?: string;
        address?: string;
        ip?: string;
        porta?: number;
        printerName?: string;
    };
    quantidadeVias: number;
    origem: string;
    tentativas?: number;
}

interface PrintQueueContextData {
    adicionarNaFila: (job: Omit<PrintJob, 'id'>) => void;
    filaAtual: PrintJob[];
    imprimindoAgora: string | null;
    limparFila: () => void;
}

const PrintQueueContext = createContext<PrintQueueContextData>({} as PrintQueueContextData);

export const PrintQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const filaImpressao = useRef<PrintJob[]>([]);
    const imprimindoAtualmente = useRef(false);
    const [filaAtual, setFilaAtual] = useState<PrintJob[]>([]);
    const [imprimindoAgora, setImprimindoAgora] = useState<string | null>(null);

    const gerarId = () => `print_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const adicionarNaFila = (job: Omit<PrintJob, 'id'>) => {
        const novoJob: PrintJob = { ...job, id: gerarId(), tentativas: 0 };

        filaImpressao.current.push(novoJob);
        setFilaAtual([...filaImpressao.current]);

        console.log(`📋 [FILA] Adicionado ${job.tipo.toUpperCase()} (${job.origem}):`, {
            id: novoJob.id,
            vias: job.quantidadeVias,
            posicaoFila: filaImpressao.current.length
        });

        processarFilaDeImpressao();
    };

    // Exporta adicionarNaFila via helper global ao montar o provider
    useEffect(() => {
        setPrintQueueInstance(adicionarNaFila);
        return () => setPrintQueueInstance(() => { });
    }, []);

    const processarFilaDeImpressao = async () => {
        if (imprimindoAtualmente.current || filaImpressao.current.length === 0) {
            return;
        }

        imprimindoAtualmente.current = true;
        const job = filaImpressao.current[0]; // NÃO remove ainda
        setImprimindoAgora(job.id);

        // Incrementa tentativas
        job.tentativas = (job.tentativas || 0) + 1;
        const MAX_TENTATIVAS = 3;

        console.log(`🖨️ [FILA] Iniciando ${job.tipo.toUpperCase()} (${job.origem}) - Tentativa ${job.tentativas}/${MAX_TENTATIVAS}`, {
            id: job.id,
            restantes: filaImpressao.current.length - 1
        });

        try {
            switch (job.tipo) {
                case 'ble':
                    await imprimirBLE(job);
                    break;
                case 'spp':
                    await imprimirSPP(job);
                    break;
                case 'rede':
                    await imprimirRede(job);
                    break;
                case 'local':
                    await imprimirLocal(job);
                    break;
            }

            console.log(`✅ [FILA] Concluído (${job.origem}):`, job.id);

            // Remove apenas após sucesso
            filaImpressao.current.shift();
            setFilaAtual([...filaImpressao.current]);

        } catch (error: any) {
            const errorMsg = error?.message || 'Erro desconhecido';
            console.error(`❌ [FILA] Falha (${job.tentativas}/${MAX_TENTATIVAS}) em ${job.tipo.toUpperCase()}:`, errorMsg);

            if ((job.tentativas || 0) < MAX_TENTATIVAS) {
                // Backoff exponencial: 4s, 8s, 12s
                const nextDelay = 4000 * (job.tentativas || 1);
                console.log(`🔄 [FILA] Próximo retry em ${nextDelay / 1000}s...`);

                imprimindoAtualmente.current = false;
                setImprimindoAgora(null);

                await new Promise(resolve => setTimeout(resolve, nextDelay));
                processarFilaDeImpressao(); // Tenta novamente
                return; // Importante: retorna aqui para não executar o finally

            } else {
                console.log(`🚫 [FILA] Desistindo após ${MAX_TENTATIVAS} tentativas - ${job.tipo.toUpperCase()} (${job.origem})`);

                // Remove da fila após esgotar tentativas
                filaImpressao.current.shift();
                setFilaAtual([...filaImpressao.current]);

                // TODO: Notificar usuário via Toast
                // Toast.show({ text: `Falha na impressão após ${MAX_TENTATIVAS} tentativas. Verifique a impressora.`, duration: "long" });
            }
        } finally {
            imprimindoAtualmente.current = false;
            setImprimindoAgora(null);
            await new Promise(resolve => setTimeout(resolve, 1000));
            processarFilaDeImpressao();
        }
    };

    const imprimirBLE = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { deviceId } = impressoraConfig;

        if (!deviceId) {
            throw new Error('Device ID não configurado');
        }

        const PRINT_SERVICE_UUID = "000018f0-0000-1000-8000-00805f9b34fb";
        const PRINT_CHARACTERISTIC_UUID = "00002af1-0000-1000-8000-00805f9b34fb";
        const chunkSize = 20;
        const buffer = comandosEscPos.buffer;
        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        // Verifica conexão
        let isConnected = false;
        try {
            const connectedDevices = await BleClient.getConnectedDevices([]);
            isConnected = connectedDevices.some(device => device.deviceId === deviceId);
            console.log(`🔌 [BLE] Estado: ${isConnected ? 'CONECTADO' : 'DESCONECTADO'}`);
        } catch {
            isConnected = false;
        }

        // Conecta se necessário
        if (!isConnected) {
            console.log(`🔌 [BLE] Conectando...`);
            await BleClient.connect(deviceId);
            await delay(1000);
            console.log(`✅ [BLE] Conectado`);
        }

        // Imprime cada via
        try {
            for (let via = 0; via < quantidadeVias; via++) {
                console.log(`📄 [BLE] Imprimindo via ${via + 1}/${quantidadeVias}`);

                for (let i = 0; i < buffer.byteLength; i += chunkSize) {
                    const chunk = new DataView(buffer.slice(i, Math.min(i + chunkSize, buffer.byteLength)));
                    await BleClient.write(deviceId, PRINT_SERVICE_UUID, PRINT_CHARACTERISTIC_UUID, chunk);
                    await delay(50);
                }

                if (via < quantidadeVias - 1) {
                    await delay(2000);
                }
            }
        } finally {
            // Sempre desconecta ao final
            try {
                await BleClient.disconnect(deviceId);
                console.log(`🔌 [BLE] Desconectado`);
            } catch (error) {
                console.log(`⚠️ [BLE] Erro ao desconectar:`, error);
            }
        }
    };

    const imprimirSPP = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { address } = impressoraConfig;

        if (!address) {
            throw new Error('Endereço SPP não configurado');
        }

        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        // Verifica conexão
        let isConnected = false;
        try {
            isConnected = await BluetoothSerial.isConnected();
            console.log(`🔌 [SPP] Estado: ${isConnected ? 'CONECTADO' : 'DESCONECTADO'}`);
        } catch {
            isConnected = false;
        }

        // Conecta se necessário
        if (!isConnected) {
            console.log(`🔌 [SPP] Conectando...`);
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Timeout SPP')), 10000);

                BluetoothSerial.connect(address).subscribe(
                    async () => {
                        clearTimeout(timeout);
                        await delay(1500);
                        resolve();
                    },
                    (error: any) => {
                        clearTimeout(timeout);
                        reject(error);
                    }
                );
            });
            console.log(`✅ [SPP] Conectado`);
        }

        // Converte para string
        const dataString = Array.from(comandosEscPos)
            .map(byte => String.fromCharCode(byte))
            .join('');

        // Imprime cada via
        try {
            for (let via = 0; via < quantidadeVias; via++) {
                console.log(`📄 [SPP] Imprimindo via ${via + 1}/${quantidadeVias}`);
                await BluetoothSerial.write(dataString);

                if (via < quantidadeVias - 1) {
                    await delay(2000);
                }
            }
        } finally {
            // Sempre desconecta ao final
            try {
                await BluetoothSerial.disconnect();
                console.log(`🔌 [SPP] Desconectado`);
            } catch (error) {
                console.log(`⚠️ [SPP] Erro ao desconectar:`, error);
            }
        }
    };

    const imprimirRede = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { ip, porta } = impressoraConfig;

        if (!ip || !porta) {
            throw new Error('IP/Porta não configurados');
        }

        return new Promise<void>((resolve, reject) => {
            let viasImpressas = 0;

            const imprimirVia = () => {
                (window as any).chrome.sockets.tcp.create({}, (createInfo: any) => {
                    const socketId = createInfo.socketId;

                    const timeoutId = setTimeout(() => {
                        (window as any).chrome.sockets.tcp.close(socketId);
                        reject(new Error('Timeout Rede'));
                    }, 3000);

                    (window as any).chrome.sockets.tcp.connect(socketId, ip, porta, (result: number) => {
                        clearTimeout(timeoutId);

                        if (result === 0) {
                            console.log(`📄 [REDE] Imprimindo via ${viasImpressas + 1}/${quantidadeVias}`);
                            (window as any).chrome.sockets.tcp.send(socketId, comandosEscPos.buffer, () => {
                                (window as any).chrome.sockets.tcp.close(socketId);
                                viasImpressas++;

                                if (viasImpressas < quantidadeVias) {
                                    setTimeout(imprimirVia, 2000);
                                } else {
                                    resolve();
                                }
                            });
                        } else {
                            (window as any).chrome.sockets.tcp.close(socketId);
                            reject(new Error('Falha conexão Rede'));
                        }
                    });
                });
            };

            imprimirVia();
        });
    };

    const imprimirLocal = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { printerName } = impressoraConfig;

        if (!printerName) {
            throw new Error('Nome da impressora não configurado');
        }

        const dataString = Array.from(comandosEscPos)
            .map(byte => String.fromCharCode(byte))
            .join('');

        const enviarParaImpressora = async () => {
            const response = await fetch("http://localhost:9100/print", {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ printer: printerName, data: dataString })
            });

            if (!response.ok) {
                throw new Error('Falha impressão Local');
            }
        };

        for (let via = 0; via < quantidadeVias; via++) {
            console.log(`📄 [LOCAL] Imprimindo via ${via + 1}/${quantidadeVias}`);
            await enviarParaImpressora();

            if (via < quantidadeVias - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    };

    const limparFila = () => {
        filaImpressao.current = [];
        setFilaAtual([]);
        console.log(`🗑️ [FILA] Limpa`);
    };

    return (
        <PrintQueueContext.Provider
            value={{
                adicionarNaFila,
                filaAtual,
                imprimindoAgora,
                limparFila
            }}
        >
            {children}
        </PrintQueueContext.Provider>
    );
};

export const usePrintQueue = () => {
    const context = useContext(PrintQueueContext);
    if (!context) {
        throw new Error('usePrintQueue deve ser usado dentro de PrintQueueProvider');
    }
    return context;
};
